import { useState } from "react";
import { ActionHandler, NameInput, StateColumnMultiple, StateColumnSingle } from "@/components/UI/tools";
import { Field, SelectField, TextField } from "@/app/workspace/settings/ui";
import { Add_new, Edit, Remove, Save } from "@/components/UI/svgs";
import { GallerySection, Specimen, noop } from "../galleryKit";

const OPTIONS = [
    { id: 1, name: "Option one" },
    { id: 2, name: "Option two" },
    { id: 3, name: "Option three" },
];

const ButtonsDemo = () => (
    <div className="flex flex-col gap-1">
        <div className="flex flex-row flex-wrap items-center">
            <ActionHandler funq={noop}>Default</ActionHandler>
            <ActionHandler funq={noop} selected>Selected</ActionHandler>
            <ActionHandler funq={noop} glow>Glow</ActionHandler>
            <ActionHandler funq={noop} disabled>Disabled</ActionHandler>
        </div>
        <div className="flex flex-row flex-wrap items-center">
            <ActionHandler funq={noop} className="btn-primary">Primary</ActionHandler>
            <ActionHandler funq={noop} className="btn-soft">Soft</ActionHandler>
            <ActionHandler funq={noop} className="btn-ghost">Ghost</ActionHandler>
            <ActionHandler funq={noop} className="btn-danger">Danger</ActionHandler>
            <ActionHandler funq={noop} className="btn-danger-solid">Danger solid</ActionHandler>
            <ActionHandler funq={noop} className="btn-record">Record</ActionHandler>
        </div>
        <div className="flex flex-row flex-wrap items-center">
            <ActionHandler funq={noop} title="Add"><Add_new /></ActionHandler>
            <ActionHandler funq={noop} title="Edit"><Edit /></ActionHandler>
            <ActionHandler funq={noop} title="Save"><Save /></ActionHandler>
            <ActionHandler funq={noop} title="Remove" className="btn-ghost"><Remove /></ActionHandler>
            <ActionHandler funq={noop} className="btn-soft btn-sm">Small</ActionHandler>
            <ActionHandler funq={noop} className="btn-ghost btn-mono">mono</ActionHandler>
            <span className="meta flex flex-row items-center gap-[.35rem]">
                Meta line
                <ActionHandler funq={noop} title="Edit" className="btn-ghost btn-meta"><Edit /></ActionHandler>
            </span>
        </div>
        <div className="w-[16rem]">
            <ActionHandler funq={noop} className="btn-primary btn-block">Block</ActionHandler>
        </div>
    </div>
);

const InputsDemo = () => {
    const [text, setText] = useState("");
    const [select, setSelect] = useState<number | undefined>(1);
    return (
        <div className="flex flex-col gap-1 max-w-[28rem]">
            <div className="flex flex-row gap-1">
                <Field label="Text field">
                    <TextField value={text} onChange={setText} placeholder="Placeholder" />
                </Field>
                <Field label="Select field">
                    <SelectField value={select} onChange={setSelect} options={OPTIONS} />
                </Field>
            </div>
            <Field label="Read-only">
                <TextField value="Read-only value" onChange={noop} readOnly />
            </Field>
            <Field label="Invalid">
                <input className="input input-invalid" defaultValue="Invalid value" />
            </Field>
            <Field label="Textarea">
                <textarea className="textarea" placeholder="Multi-line text" />
            </Field>
            <Field label="Mono">
                <textarea className="textarea input-mono" defaultValue={"prompt.key = value"} />
            </Field>
            <div className="flex flex-row items-center gap-1">
                <span className="hint">Inline select</span>
                <select className="select-inline" defaultValue="a">
                    <option value="a">Option A</option>
                    <option value="b">Option B</option>
                </select>
            </div>
        </div>
    );
};

const SearchDemo = () => {
    const [active, setActive] = useState(false);
    return (
        <div className="flex flex-col gap-[.35rem] max-w-[22rem]">
            <input className="search" placeholder="Filter…" />
            <span className={`filter-toggle ${active ? "filter-toggle-active" : ""}`} onClick={() => setActive(!active)}>
                missing only
            </span>
        </div>
    );
};

const ToggleTabsDemo = () => {
    const [on, setOn] = useState(true);
    const [tab, setTab] = useState("first");
    return (
        <div className="flex flex-col gap-1">
            <div className="flex flex-row items-center gap-1">
                <button role="switch" aria-checked={on} className={`toggle ${on ? "toggle-on" : ""}`} onClick={() => setOn(!on)} />
                <button role="switch" aria-checked={false} className="toggle" disabled />
            </div>
            <div className="tabs">
                {["first", "second", "third"].map((t) => (
                    <button key={t} className={`tab ${tab === t ? "tab-active" : ""}`} onClick={() => setTab(t)}>{t}</button>
                ))}
            </div>
        </div>
    );
};

const NameInputDemo = () => (
    <div className="flex flex-col gap-1 max-w-[28rem]">
        <NameInput type="text" label="Name" value="Entry name" onChange={noop} validator={() => true} onValidChange={noop} edit={false} />
        <NameInput type="text" label="Name" value="Entry name (editing)" onChange={noop} validator={() => true} onValidChange={noop} edit />
        <NameInput type="text" label="Name" value="" onChange={noop} validator={(v) => v.length > 0} onValidChange={noop} edit />
    </div>
);

const ValueBoxDemo = () => {
    const [single, setSingle] = useState<number | undefined>(1);
    const [multi, setMulti] = useState<number[]>([1, 3]);
    return (
        <div className="flex flex-row flex-wrap gap-1">
            <StateColumnSingle type="single" label="Single" items={OPTIONS} value={single} onChange={setSingle} validator={() => true} onValidChange={noop} edit />
            <StateColumnMultiple type="multiple" label="Multiple" items={OPTIONS} value={multi} onChange={setMulti} validator={() => true} onValidChange={noop} edit />
            <StateColumnSingle type="single" label="Required" items={OPTIONS} value={undefined} onChange={noop} validator={(v) => v != null} onValidChange={noop} edit />
            <StateColumnSingle type="single" label="Read-only" items={OPTIONS} value={2} onChange={noop} validator={() => true} onValidChange={noop} edit={false} />
        </div>
    );
};

const PopoverDemo = () => (
    <div className="popover !static !max-h-none w-[14rem]">
        <input className="popover-filter" placeholder="Filter…" />
        {OPTIONS.map((o, i) => (
            <button key={o.id} className={`option ${i === 0 ? "option-selected" : ""}`}>
                <span className={`option-indicator ${i === 0 ? "option-indicator-selected" : ""}`} />
                <span className="option-label">{o.name}</span>
            </button>
        ))}
    </div>
);

export const ControlsSection = () => (
    <GallerySection id="controls" title="Controls" intro="Everything the user clicks or types into.">
        <Specimen
            name="Button"
            classes={["btn", "btn-selected", "btn-glow", "btn-primary", "btn-soft", "btn-ghost", "btn-danger", "btn-danger-solid", "btn-record", "btn-sm", "btn-block", "btn-mono", "btn-meta"]}
            usage="The single button. Render it through ActionHandler, which adds keyboard shortcuts; pass modifiers with className."
            rules={[
                "One btn-primary per view, for the main action. Everything else is the default outline, soft or ghost.",
                "Inside a nav-group or toolbar use btn-ghost.",
                "Icon-only buttons need a title.",
                "btn-meta (with btn-ghost) is an icon action inside a line of text or a table cell; it doesn't grow the line.",
                "btn-glow is for the next expected step (e.g. start recording), not for decoration.",
            ]}
            code={`<ActionHandler funq={save} keysTrigger="ctrlKey + s" className="btn-primary">Save</ActionHandler>
<ActionHandler funq={close} className="btn-ghost" title="Close"><Close /></ActionHandler>`}
            demo={() => <ButtonsDemo />}
        />
        <Specimen
            name="Text input, select, textarea"
            classes={["field", "field-label", "input", "select", "textarea", "input-mono", "input-invalid", "select-inline"]}
            usage="Form controls share one look. Wrap each in Field to get the label; use TextField / SelectField from settings/ui where they fit."
            rules={[
                "Read-only inputs drop their border automatically ([readonly]).",
                "Use input-mono for keys, prompts and code.",
                "select-inline is for a picker sitting in a line of text, not in forms.",
            ]}
            code={`<Field label="Name"><TextField value={name} onChange={setName} /></Field>
<textarea className="textarea input-mono" />`}
            demo={() => <InputsDemo />}
        />
        <Specimen
            name="Search and filter toggle"
            classes={["search", "filter-toggle", "filter-toggle-active"]}
            usage="The filter box above lists and trees, and the text toggle that narrows it."
            demo={() => <SearchDemo />}
        />
        <Specimen
            name="Toggle and tabs"
            classes={["toggle", "toggle-on", "tabs", "tab", "tab-active"]}
            usage="toggle is an on/off switch on a single button element. tabs switch between views of the same object."
            code={`<button role="switch" aria-checked={on} className={\`toggle \${on ? "toggle-on" : ""}\`} />`}
            demo={() => <ToggleTabsDemo />}
        />
        <Specimen
            name="Name input"
            classes={["input", "input-entry", "input-invalid"]}
            usage="Entry title and single-line entry fields (e.g. Location). Same typography as .input; plain text until the entry enters edit mode, then shows its input frame. Rendered by NameInput / EntryItem."
            demo={() => <NameInputDemo />}
        />
        <Specimen
            name="Value box"
            classes={["value-box", "value-box-open", "value-box-invalid", "value-box-label", "chip", "icon-btn"]}
            usage="Labelled box showing the current value(s) as chips, with + to open a filterable option list. Rendered by StateColumnSingle / Multiple / Switch / DateTimeRange."
            demo={() => <ValueBoxDemo />}
        />
        <Specimen
            name="Popover and option"
            classes={["popover", "popover-filter", "option", "option-selected", "option-label", "option-indicator", "option-indicator-selected"]}
            usage="Floating list anchored to a control, portaled into body. Card chrome with a filter header; each choice is an option, which is a card row (same padding, divider, hover and selected tint) with an indicator dot."
            demo={() => <PopoverDemo />}
        />
    </GallerySection>
);
